import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useParams } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Camera, Save, Check, FileText, Upload, MessageSquare, Send, Clock, AlertCircle, CheckCircle, X, ChevronDown, ChevronUp, Edit, Loader2, Trash2, User, Eye, Search } from "lucide-react";
import CertificationModal from "./certification-modal";
import ValuationResults from "../components/ValuationResults";
import { useValuation } from "../context/ValuationContext";
import { useFormContext } from "react-hook-form";
import { ValuationFormValues } from "@/store/valuationFormStore";
import { useLiveFormData } from "@/hooks/useLiveFormData";

interface ValuationSidebarProps {
  id?: string;
  property?: any;
  onValuationComplete?: () => void;
  mode?: "collector" | "supervisor";
}

interface Comment {
  id: string;
  message: string | null;
  commentType: string;
  section: string;
  isResolved: boolean;
  resolvedAt: string | null;
  authorId: string;
  authorRole: string;
  createdAt: string;
  updatedAt: string;
  authorUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    profileImageUrl: string | null;
  } | null;
}

interface FeedbackMessage {
  id: string;
  message: string;
  priority: "P1" | "P2" | "P3";
  sender: string;
  senderRole: "supervisor" | "collector";
  timestamp: string;
  isRead: boolean;
  collectorId?: string;
  collectorName?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";
const token = localStorage.getItem("valuation_token");

const getPriorityConfig = (priority: "P1" | "P2" | "P3") => {
  switch (priority) {
    case "P1":
      return {
        icon: AlertCircle,
        bg: "bg-red-50",
        text: "text-red-800",
        border: "border-red-200",
      };
    case "P2":
      return {
        icon: AlertCircle,
        bg: "bg-yellow-50",
        text: "text-yellow-800",
        border: "border-yellow-200",
      };
    case "P3":
      return {
        icon: CheckCircle,
        bg: "bg-blue-50",
        text: "text-blue-800",
        border: "border-blue-200",
      };
  }
};

export default function ValuationSidebar({
  property,
  mode = "collector",
}: ValuationSidebarProps) {
  const isSupervisor = mode === "supervisor";

  console.log("ValuationSidebar rendered with property:~~~", property, "mode:", mode);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const { valuationResults, formatCurrency } = useValuation();
  const { id } = useParams<{ id: string }>();
  const { combinedData } = useLiveFormData();

  // Country & currency detection — defaults to UGX, RWF for Rwanda
  // const country = property?.location?.country ?? property?.country;
  // const isRwanda = country === "RW" || country?.toLowerCase() === "rwanda";
  // const currency = isRwanda ? "RWF" : "UGX";

    // Country & currency detection — defaults to UGX, RWF for Rwanda
  const country = combinedData?.country;
  const isRwanda = country === "RW" || country?.toLowerCase() === "rwanda";
  const currency = isRwanda ? "RWF" : "UGX";
  
 const formatLocalCurrency = (value: number) => {
    if (!value && value !== 0) return `${currency} 0`;
    return `${currency} ${Number(value).toLocaleString()}`;
  };

//  const formatLocalCurrency = (value: number) => {
//     if (!value && value !== 0) return `${currency} 0`;
//     return `${currency} ${Number(value).toLocaleString()}`;
//   };

  // Comments & Feedback State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showQuickCommentModal, setShowQuickCommentModal] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState<string | null>(null);

  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackPriority, setFeedbackPriority] = useState<"P1" | "P2" | "P3">("P1");
  const [quickComment, setQuickComment] = useState("");
  const [commentSection, setCommentSection] = useState("valuation");

  const [showFeedbackHistory, setShowFeedbackHistory] = useState(false);
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<"all" | "P1" | "P2" | "P3">("all");

  // New states for enhanced comments view
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [commentSearch, setCommentSearch] = useState("");
  const [commentSectionFilter, setCommentSectionFilter] = useState("all");
  const [commentStatusFilter, setCommentStatusFilter] = useState<"all" | "resolved" | "unresolved">("all");
  const [hoveredComment, setHoveredComment] = useState<string | null>(null);

  // Fetch Comments
  const fetchComments = async () => {
    if (!id) return;

    try {
      setIsLoadingComments(true);
      const response = await fetch(
        `${API_BASE_URL}/comments/properties/${id}?limit=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Failed to get comments");
      }

      setComments(result.data.comments || []);
    } catch (error) {
      console.error("Comments fetch error:", error);
      toast({
        title: "Error",
        description: "Failed to load comments",
        variant: "destructive",
      });
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Fetch Feedback
  const fetchFeedback = async () => {
    if (!id) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/feedback?id=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch feedback");

      const data = await response.json();
      setFeedbackMessages(Array.isArray(data) ? data : []);
      setUnreadCount(data.filter((msg: FeedbackMessage) => !msg.isRead).length);
    } catch (error) {
      console.error("Feedback fetch error:", error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchComments();
      fetchFeedback();
    }
  }, [id]);

  // Helper Functions
  const getTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleRefresh = () => {
    fetchComments();
    fetchFeedback();
    toast({ title: "Refreshed", description: "Data updated" });
  };

  // Comment Actions
  const handleAddQuickComment = async () => {
    if (!quickComment.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: quickComment,
          section: commentSection,
          id: property?.id || "",
        }),
      });

      if (!response.ok) throw new Error("Failed to create comment");

      const newComment = await response.json();
      setComments((prev) => [newComment, ...prev]);
      setQuickComment("");
      setShowQuickCommentModal(false);

      toast({ title: "Success", description: "Comment added" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
    }
  };

  const handleResolveComment = async (commentId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/comments/${commentId}/resolve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isResolved: true }),
        }
      );

      if (!response.ok) throw new Error("Failed to resolve");

      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, isResolved: true } : c))
      );

      toast({ title: "Success", description: "Comment resolved" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not resolve comment",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm("Delete this comment?")) return;

    setIsDeletingComment(commentId);
    try {
      const response = await fetch(`${API_BASE_URL}/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Delete failed");

      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast({ title: "Success", description: "Comment deleted" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete comment",
        variant: "destructive",
      });
    } finally {
      setIsDeletingComment(null);
    }
  };

  const toggleCommentExpand = (commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  // Feedback Actions
  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast({
        title: "Error",
        description: "Please write a message",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingFeedback(true);

    try {
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: feedbackMessage,
          priority: feedbackPriority,
          id: property?.id || "",
          senderRole: "COLLECTOR",
        }),
      });

      if (!response.ok) throw new Error("Failed to send feedback");

      const newFeedback = await response.json();
      setFeedbackMessages((prev) => [newFeedback, ...prev]);
      setUnreadCount((prev) => prev + 1);
      setFeedbackMessage("");
      setShowFeedbackModal(false);

      toast({ title: "Success", description: "Feedback sent" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send feedback",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/feedback/${messageId}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setFeedbackMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, isRead: true } : m))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Mark read failed:", err);
    }
  };

  // Filter Comments
  const filteredComments = comments.filter(comment => {
    // Search filter
    if (commentSearch && comment.message) {
      const searchLower = commentSearch.toLowerCase();
      const messageLower = comment.message.toLowerCase();
      if (!messageLower.includes(searchLower)) return false;
    }

    // Section filter
    if (commentSectionFilter !== "all" && comment.section !== commentSectionFilter) {
      return false;
    }

    // Status filter
    if (commentStatusFilter === "resolved" && !comment.isResolved) return false;
    if (commentStatusFilter === "unresolved" && comment.isResolved) return false;

    return true;
  });

  // Keep the existing valuation logic...
  const [valuation, setValuation] = useState({
    landValue: 0,
    buildingReplacementCost: 0,
    depreciatedValue: 0,
    amenitiesValue: 0,
    totalValue: 0,
    confidenceLevel: 0,
  });

  useEffect(() => {
    if (isSupervisor && property?.valuationDetails) {
      setValuation({
        landValue: property.valuationDetails.landValue || 0,
        buildingReplacementCost: property.valuationDetails.buildingValue || 0,
        depreciatedValue: property.valuationDetails.depreciationValue || 0,
        amenitiesValue: property.valuationDetails.amenitiesValue || 0,
        totalValue: property.valuationDetails.totalPropertyValue || 0,
        confidenceLevel: 100,
      });
    }
  }, [property, isSupervisor]);

  useEffect(() => {
    if (!isSupervisor && combinedData) {
      const landArea = Number(combinedData.landArea) || 0;
      const landRate = Number(combinedData.landRate) || 0;
      const builtArea = Number(combinedData.mainHouseArea) || 0;
      const yearBuilt = combinedData.yearOfConstruction
        ? Number(combinedData.yearOfConstruction)
        : new Date().getFullYear();

      const landValue = landArea * landRate;
      const totalDepreciation = Math.min(
        0.8,
        combinedData.effectiveAge * combinedData.depreciationRate
      );
      const buildingReplacementCost = Number(combinedData.mainHouseArea) * combinedData.mainHouseRate;
      
      let amenitiesValue = 0;
      if (combinedData.annexHouseArea) amenitiesValue += (combinedData.annexHouseArea*combinedData.annexRate);
      if (combinedData.fenceLength) amenitiesValue += (combinedData.fenceLength*combinedData.fenceRate);
      if (combinedData.gardenArea) amenitiesValue += (combinedData.gardenArea*combinedData.gardenRate);
      if (combinedData.retainingWallsLength) amenitiesValue += (combinedData.retainingWallsLength*combinedData.retainingWallRate);
      if (combinedData.entranceGateLength) amenitiesValue += (combinedData.entranceGateLength*combinedData.entranceGateRate);
      if (combinedData.pavedArea) amenitiesValue += (combinedData.pavedArea*combinedData.pavedAreaRate);
      if (combinedData.septicTankNumber) amenitiesValue += (combinedData.septicTankNumber*combinedData.septicTankValue);
      if (combinedData.verandahArea) amenitiesValue += (combinedData.verandahArea*combinedData.verandahRate);

      const depreciatedValue = buildingReplacementCost + amenitiesValue;

      const totalValue = landValue + depreciatedValue;

      const requiredFields = [
        combinedData.upi,
        combinedData.ownerIdNumber,
        combinedData.province,
        combinedData.district,
        landArea,
        landRate,
        builtArea,
        yearBuilt,
        combinedData.roofType,
        combinedData.wallMaterial
      ];
      const completedFields = requiredFields.filter((field) => {
        if (typeof field === "number") return field > 0;
        return field !== undefined && field !== null && field !== "";
      }).length;

      const confidenceLevel = Math.round(
        (completedFields / requiredFields.length) * 100
      );
      const newValuation = {
        landValue,
        buildingReplacementCost,
        depreciatedValue,
        amenitiesValue,
        totalValue,
        confidenceLevel,
      };
      setValuation((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(newValuation)) return prev;
        return newValuation;
      });
    }
  }, [combinedData, isSupervisor]);

  return (
    <>
      {/* Valuation Summary */}
      <Card className="bg-white p-4  rounded-lg shadow-sm border border-gray-200 mb-6">
        <CardHeader className="px-6 py-4 border-b border-gray-200">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Live Valuation
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Land Value</span>
              <span className="font-semibold text-gray-900">
                {formatLocalCurrency(valuation.landValue)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Building Cost</span>
              <span className="font-semibold text-gray-900">
                {formatLocalCurrency(valuation.buildingReplacementCost)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Depreciated Value</span>
              <span className="font-semibold text-gray-900">
                {formatLocalCurrency(valuation.depreciatedValue)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Amenities</span>
              <span className="font-semibold text-gray-900">
                {formatLocalCurrency(valuation.amenitiesValue)}
              </span>
            </div>
            <hr className="border-gray-200" />
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold text-gray-900">
                Total Value
              </span>
              <span className="text-xl font-bold text-primary">
                {formatLocalCurrency(valuation.totalValue)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
      <ValuationResults />
      
      {/* Action Buttons */}
      <div className="space-y-3 mb-6">
        {/* Existing action buttons remain here */}
      </div>

      {/* Comments & Feedback Card - ADDED SECTION */}
      <Card className="bg-white shadow-sm border border-gray-200 mb-6">
        <CardHeader className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold">Comments & Feedback</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoadingComments}>
              {isLoadingComments ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Refresh"
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Comments Header with Filters and Actions */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium text-gray-900">
                  Comments ({filteredComments.length})
                </h4>
              </div>
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowQuickCommentModal(true)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Add Comment
              </Button>
            </div>

            {/* Comments Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
              <select
                value={commentSectionFilter}
                onChange={(e) => setCommentSectionFilter(e.target.value)}
                className="px-1 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sections</option>
                <option value="valuation">Valuation</option>
                <option value="land">Land</option>
                <option value="building">Building</option>
                <option value="amenities">Amenities</option>
                <option value="general">General</option>
              </select>
              
              <select
                value={commentStatusFilter}
                onChange={(e) => setCommentStatusFilter(e.target.value as any)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {/* Comments List */}
            {isLoadingComments ? (
              <div className="text-center py-10">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-3 text-sm text-gray-500">Loading comments...</p>
              </div>
            ) : filteredComments.length > 0 ? (
              <div className={`space-y-4 ${isCommentsExpanded ? '' : 'max-h-[500px]'} overflow-y-auto pr-2`}>
                {filteredComments.map((comment) => {
                  const isOwn = comment.authorRole === "supervisor";
                  const isExpanded = expandedComments.has(comment.id);
                  const authorName = comment.authorUser
                    ? `${comment.authorUser.firstName} ${comment.authorUser.lastName || ""}`.trim()
                    : "Unknown User";
                  const message = comment.message || "";
                  const shouldTruncate = message.length > 200 && !isExpanded;
                  const displayMessage = shouldTruncate 
                    ? message.slice(0, 200) + "..." 
                    : message;

                  return (
                    <div
                      key={comment.id}
                      className={`p-4 rounded-lg border transition-all duration-200 ${
                        comment.isResolved
                          ? "bg-gray-50 border-gray-200"
                          : "bg-blue-50/60 border-blue-200 hover:bg-blue-50"
                      } ${isOwn ? "border-l-4 border-l-indigo-500" : ""} ${
                        hoveredComment === comment.id ? "shadow-md" : ""
                      }`}
                      onMouseEnter={() => setHoveredComment(comment.id)}
                      onMouseLeave={() => setHoveredComment(null)}
                    >
                      <div className="flex justify-between items-start mb-2 text-xs leading-tight text-gray-700 whitespace-pre-wrap">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User className="h-2 w-2 text-indigo-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {authorName}
                              </p>
                              {isOwn && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                  You
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                comment.section === 'valuation' ? 'bg-purple-100 text-purple-800' :
                                comment.section === 'land' ? 'bg-green-100 text-green-800' :
                                comment.section === 'building' ? 'bg-amber-100 text-amber-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {comment.section}
                              </span>
                              {comment.isResolved && (
                                <span className="inline-flex items-center gap-1 text-xs px-1 py-0.5 bg-green-100 text-green-800 rounded-full">
                                  <CheckCircle className="h-2 w-2" />
                                  Resolved
                                </span>
                              )}
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="h-1 w-1 mr-0.5" />
                              {getTimeAgo(comment.createdAt)}
                              {comment.updatedAt !== comment.createdAt && (
                                <span className="ml-2 italic">(edited)</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {isSupervisor && (
                          <div className="flex gap-1 ml-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-2 w-2 hover:bg-blue-100"
                              onClick={() => setSelectedComment(comment)}
                              title="View full comment"
                            >
                              <Eye className="h-2 w-2 text-gray-600" />
                            </Button>
                            {!comment.isResolved && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-2 w-2 mx-2 hover:bg-green-100"
                                onClick={() => handleResolveComment(comment.id)}
                                title="Mark as resolved"
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mt-3 ml-3">
                        {message ? (
                         <div className="text-xs leading-tight text-gray-700 whitespace-pre-wrap">
                            {displayMessage}
                            {shouldTruncate && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800 ml-2"
                                onClick={() => toggleCommentExpand(comment.id)}
                              >
                                Read more
                              </Button>
                            )}
                            {isExpanded && message.length > 200 && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-gray-600 hover:text-gray-800 ml-2"
                                onClick={() => toggleCommentExpand(comment.id)}
                              >
                                Show less
                              </Button>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm italic text-gray-500">[No message content]</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed border-gray-300 rounded-lg">
                <MessageSquare className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  {commentSearch || commentSectionFilter !== "all" || commentStatusFilter !== "all"
                    ? "No comments match your filters"
                    : "No comments yet"}
                </p>
                <Button
                  variant="link"
                  className="mt-2 text-blue-600"
                  onClick={() => setShowQuickCommentModal(true)}
                >
                  Add your first comment
                </Button>
              </div>
            )}
          </div>

          {/* View Toggle Button */}
          {filteredComments.length > 3 && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
                className="flex items-center gap-2"
              >
                {isCommentsExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Show Less Comments
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Show More Comments
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Supervisor Actions */}
          {isSupervisor && (
            <div className="pt-6 border-t border-gray-200 mt-6">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowFeedbackModal(true)}
              >
                <Send className="h-4 w-4 mr-2" />
                Send Feedback to Collector
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feedback History */}
      {isSupervisor && feedbackMessages.length > 0 && (
        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-600" />
                <CardTitle className="text-base font-semibold">Feedback History</CardTitle>
                {unreadCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFeedbackHistory(!showFeedbackHistory)}
              >
                {showFeedbackHistory ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Show
                  </>
                )}
              </Button>
            </div>
          </CardHeader>

          {showFeedbackHistory && (
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <input
                  type="text"
                  placeholder="Search feedback..."
                  value={feedbackSearch}
                  onChange={(e) => setFeedbackSearch(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value as any)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Priorities</option>
                  <option value="P1">P1 - Critical</option>
                  <option value="P2">P2 - Important</option>
                  <option value="P3">P3 - Suggestion</option>
                </select>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {feedbackMessages
                  .filter(
                    (m) =>
                      (priorityFilter === "all" || m.priority === priorityFilter) &&
                      (feedbackSearch === "" ||
                        m.message.toLowerCase().includes(feedbackSearch.toLowerCase()) ||
                        m.sender.toLowerCase().includes(feedbackSearch.toLowerCase()))
                  )
                  .map((message) => {
                    const config = getPriorityConfig(message.priority);
                    const Icon = config.icon;
                    const timeAgo = getTimeAgo(message.timestamp);

                    return (
                      <div
                        key={message.id}
                        className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
                          message.isRead
                            ? config.border
                            : `border-2 ${config.border.replace("200", "400")}`
                        } ${config.bg}`}
                        onClick={() => !message.isRead && markAsRead(message.id)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}
                            >
                              <Icon className="h-3.5 w-3.5 mr-1.5" />
                              {message.priority}
                            </span>
                            {!message.isRead && (
                              <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
                            )}
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3.5 w-3.5 mr-1.5" />
                            {timeAgo}
                          </div>
                        </div>

                        <p className="text-sm text-gray-800 mb-3">{message.message}</p>

                        <div className="text-xs text-gray-600">
                          <span className="font-medium flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" />
                            {message.sender}
                          </span>
                          {message.collectorName && (
                            <div className="mt-1">
                              <span className="text-gray-400">To: </span>
                              {message.collectorName}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Modals */}
      {/* Comment Detail Modal */}
      {selectedComment && (
        <div className="fixed inset-0 z-50 bg-white overflow-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center shadow-sm">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => setSelectedComment(null)}
                className="mr-4"
              >
                Back
              </Button>
              <h2 className="text-xl font-bold">Comment Details</h2>
            </div>
            <Button variant="ghost" onClick={() => setSelectedComment(null)}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedComment.authorUser 
                      ? `${selectedComment.authorUser.firstName} ${selectedComment.authorUser.lastName}`
                      : 'Unknown User'}
                    {selectedComment.authorRole === "supervisor" && (
                      <span className="ml-2 text-sm text-indigo-600">(Supervisor)</span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedComment.createdAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-6">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  selectedComment.section === 'valuation' ? 'bg-purple-100 text-purple-800' :
                  selectedComment.section === 'land' ? 'bg-green-100 text-green-800' :
                  selectedComment.section === 'building' ? 'bg-amber-100 text-amber-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {selectedComment.section.charAt(0).toUpperCase() + selectedComment.section.slice(1)}
                </span>
                {selectedComment.isResolved ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircle className="h-4 w-4" />
                    Resolved
                    {selectedComment.resolvedAt && (
                      <span className="ml-1 text-xs">
                        on {new Date(selectedComment.resolvedAt).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <AlertCircle className="h-4 w-4" />
                    Unresolved
                  </span>
                )}
              </div>
              
              <div className="bg-white rounded-lg border p-6 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Message</h4>
                <p className="text-gray-800 whitespace-pre-wrap text-lg leading-relaxed">
                  {selectedComment.message || "[No message content]"}
                </p>
              </div>
              
              {isSupervisor && (
                <div className="flex gap-3 pt-6 border-t">
                  {!selectedComment.isResolved && (
                    <Button 
                      onClick={() => {
                        handleResolveComment(selectedComment.id);
                        setSelectedComment(null);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Resolved
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedComment(null)}
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-semibold text-gray-900">Send Feedback</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowFeedbackModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Priority</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["P1", "P2", "P3"] as const).map((p) => (
                    <Button
                      key={p}
                      type="button"
                      variant={feedbackPriority === p ? "default" : "outline"}
                      className={
                        feedbackPriority === p
                          ? p === "P1"
                            ? "bg-red-600 hover:bg-red-700"
                            : p === "P2"
                            ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                            : "bg-blue-600 hover:bg-blue-700"
                          : ""
                      }
                      onClick={() => setFeedbackPriority(p)}
                    >
                      {p}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Message</label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                  placeholder="Your feedback to the collector..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSendFeedback}
                  disabled={!feedbackMessage.trim() || isLoadingFeedback}
                >
                  {isLoadingFeedback ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Comment Modal */}
      {showQuickCommentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-semibold text-gray-900">Add Comment</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowQuickCommentModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Section</label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {["valuation", "land", "building", "amenities", "general"].map((sec) => (
                    <Button
                      key={sec}
                      type="button"
                      variant={commentSection === sec ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCommentSection(sec)}
                    >
                      {sec.charAt(0).toUpperCase() + sec.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Comment</label>
                <textarea
                  value={quickComment}
                  onChange={(e) => setQuickComment(e.target.value)}
                  className="w-full h-28 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                  placeholder={`Your comment about ${commentSection}...`}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowQuickCommentModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddQuickComment} disabled={!quickComment.trim()}>
                  <Check className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}